// Grids & Gantts — External database columns, the page's half: one hook that
// asks the External Database Sources for every value the grid's lookup
// columns need, and hands the cells an answer function (ctx.lookupOf).
//
// - One request per column group (source, table, match, value) for all its
//   values at once, 2 000 a request — a grid of thirty thousand rows asks a
//   handful of times, and the source answers from memory.
// - Only what changed is asked again: a value a row gains (an edit, a create,
//   another person's change arriving live) is asked on its own; a value
//   already answered never is.
// - ↻ (a new load) starts afresh, so a file changed in SharePoint shows.
// - A refusal (no access, no such source or column) shows as #ERR with the
//   reason, in that column only.
// - A summing column (lookup.sum) asks for EVERY matching row (`all`) and the
//   cell adds them up (lookups.js sumOfRows); a per-column one (lookup.per)
//   asks for that column too and adds up per value of it (perOfRows).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { externalSources } from '@modules/database/external-sources/frontend/api';
import { chunk, lookupCell, lookupPlan, missingValues, withAnswers } from './lookups';

const PER_REQUEST = 2000;

export default function useLookups({ cols, fieldIds, items, textsOf, slug, epoch }) {
  const [store, setStore] = useState({});
  const inFlight = useRef(new Map()); // group key → Set(values being asked)
  const seenEpoch = useRef(epoch);
  const plan = useMemo(() => lookupPlan(cols, fieldIds, items, textsOf), [cols, fieldIds, items, textsOf]);

  useEffect(() => {
    if (seenEpoch.current !== epoch) { seenEpoch.current = epoch; inFlight.current = new Map(); setStore({}); }
  }, [epoch]);

  useEffect(() => {
    plan.forEach((g) => {
      const busy = inFlight.current.get(g.key) || new Set();
      const ask = missingValues(g, store[g.key], busy);
      if (!ask.length) return;
      ask.forEach((v) => busy.add(v));
      inFlight.current.set(g.key, busy);
      chunk(ask, PER_REQUEST).forEach((part) => {
        externalSources.lookup(g.source, { table: g.table || undefined, match: g.match, values: part, returns: g.per ? [g.value, g.per] : [g.value], ...(g.sum || g.per ? { all: true } : {}) }, { consumer: slug ? `grid:${slug}` : 'grid' })
          .then((r) => setStore((s) => ({ ...s, [g.key]: withAnswers(s[g.key], part, r && r.values) })))
          .catch((e) => {
            const msg = (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'The source did not answer';
            setStore((s) => ({ ...s, [g.key]: { values: (s[g.key] && s[g.key].values) || new Map(), error: msg } }));
          })
          .finally(() => { part.forEach((v) => busy.delete(v)); });
      });
    });
  }, [plan, store]);

  return useCallback((col, value) => lookupCell(store, col, value), [store]);
}
